import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

// MUI Components
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  AppBar,
  Box,
  Button,
  Dialog,
  Drawer,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Tabs,
  Tab,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import SchoolIcon from '@mui/icons-material/School'
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium'

// MUI Icons
import CloseIcon from '@mui/icons-material/Close'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import MenuIcon from '@mui/icons-material/Menu'
import PhoneIcon from '@mui/icons-material/Phone'

// Assets
import logo from '../Assets/Logo/logo.svg'
import CollegeCertifications from '../Assets/Logo/collegecertificates.svg'
import Dost_code from '../Assets/Logo/Law_cet_code.png'
import FounderImage from '../Assets/Images/Kaka-venkataswamy-2-768x538 1.png'
// import JSONDATA from '../Assets/JSON/MV.json'

// Styles
import '../Styles/Header.css'
import '../Styles/Style.css'

// Utils / Config
import { fetchData } from '../Assets/API/apiservices'
import { ORGANIZATION_ID, SUB_ORGANIZATION_ID } from '../Config/Config'

const Header = () => {
  // Hooks and state management
  const navigate = useNavigate()
  const location = useLocation()
  const theme = useTheme()
  const headerRef = useRef(null)

  const [mobileOpen, setMobileOpen] = useState(false)
  const [expanded, setExpanded] = useState(null)
  const [activeNavItem, setActiveNavItem] = useState(null)
  const [activeSubNav, setActiveSubNav] = useState(null)
  const [isFixed, setIsFixed] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [activeSubNavItem, setActiveSubNavItem] = useState([])
  const [deptTab, setDeptTab] = useState('UG')

  // Media queries
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'))
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  // const isLargeScreen = useMediaQuery(theme.breakpoints.down(1368))
  // Data fetching

  const departmentData = {
    UG: [
      {
        title: 'B.SC',
        subdepartment: [
          { title: 'B.SC MPCS' },
          { title: 'B.SC MSDS' },
          { title: 'B.SC CND BC' },
          { title: 'B.SC Honors(It is equivalent to B.Tech CSE)' },
        ],
      },
      {
        title: 'BA',
        subdepartment: [{ title: 'BA EPP' }, { title: 'BA EPJ' }],
      },
      {
        title: 'B-Com',
        subdepartment: [
          { title: 'B.COM General' },
          { title: 'B.COM Computer Applications' },
          { title: 'B.COM Business Analytics' },
          { title: 'B.COM Finance' },
        ],
      },
      {
        title: 'BBA',
        subdepartment: [{ title: 'Business Management' }],
      },
    ],
    PG: [
      {
        title: 'M.SC',
        subdepartment: [
          { title: 'M.SC Physics' },
          { title: 'M.SC Organic Chemistry' },
          { title: 'M.SC Computer Science' },
        ],
      },
      {
        title: 'MA',
        subdepartment: [{ title: 'MA Economics' }],
      },
      {
        title: 'M.Com',
        subdepartment: [{ title: 'M.COM' }],
      },
    ],
  }

  const getDeptMenu = async () => {
    try {
      const getDepData = await fetchData(
        `webpages?organization_id=${ORGANIZATION_ID}&sub_organization_id=${SUB_ORGANIZATION_ID}`
      )
      const updatedData = [...getDepData.rows]
      if (!updatedData.some((item) => item.title === 'SAC')) {
        updatedData.push({
          id: 54,
          title: 'SAC',
          pathname: '/sac',
        })
      }
      setActiveSubNavItem(updatedData)
    } catch (err) {
      console.error('Error fetching department menu:', err)
    }
  }

  useEffect(() => {
    getDeptMenu()
  }, [])

  // Navigation items with memoization to prevent unnecessary recalculations
  const navItems = useMemo(
    () => [
      { id: 1, title: 'Home', pathname: '/home' },
      {
        id: 2,
        title: 'About',
        pathname: '/about',
        subnav_flag: true,
        subnav: [
          { title: 'AboutUs', pathname: '/aboutus' },
          { title: 'InfraStructure', pathname: '/infrastructure' },
          { title: 'IDP', pathname: '/IDPDocview' },
        ],
      },
      // { id: 3, title: 'Department', pathname: '/department' },
      {
        id: 3,
        title: 'Academics',
        subnav_flag: true,
        subnav: [
          { title: 'Courses', pathname: '/courses' },
          { title: 'Faculty', pathname: '/faculty' },
        ],
      },
      {
        id: 4,
        title: 'Activities',
        subnav_flag: true,
        subnav: activeSubNavItem.map((item) => ({
          id: item.id,
          title: item.page_name || item.title,
          pathname:
            item.pathname ||
            `/${item.page_name.toLowerCase().replace(/\s+/g, '')}/${item.id}`,
          state: { id: item.id, title: item.page_name || item.title },
        })),
      },
      { id: 5, title: 'Admission & Fee', pathname: '/Admission&Fee' },
      { id: 6, title: 'R&D Cell', pathname: '/RandDDocview' },
      { id: 7, title: 'Library', pathname: '/LibraryDocview' },
      {
        title: 'UGC Under Taking',
        pathname: '/UGCPageView',
      },
      {
        title: 'Audit Report',
        pathname: '/AuditReportPageview',
      },
      {
        id: 8,
        title: 'Student Life',
        pathname: '/studentlife',
        subnav_flag: true,
        subnav: [
          { title: 'Placements', pathname: '/placements' },
          { title: 'ICC', pathname: '/ICCDocview' },
          { title: 'Facilites For Women', pathname: '/FFCDocview' },
          { title: 'SGRC', pathname: '/SGRCDocview' },
          { title: 'Anti Ragging Cell', pathname: '/ARCDocview' },
          { title: 'Disability Facilities', pathname: '/DisabilityFacilities' },
          { title: 'Fee Refund Policy', pathname: '/FeeRefundPolicyDocview' },
        ],
      },
      {
        id: 9,
        title: 'IQAC',
        pathname: '/iqac',
        subnav_flag: true,
        subnav: [
          { title: 'About IQAC', pathname: '/aboutIQAC' },
          { title: 'Quality Policy', pathname: '/Qualitypolicy' },
          { title: 'IQAC Objectives', pathname: '/IQACObjective' },
          { title: 'Parameters of IQAC', pathname: '/IQACParameter' },
          { title: 'Working Committee', pathname: '/workingcommitee' },
          { title: 'Manatory Document', pathname: '/mandatorydoc' },
          {
            title: 'Acadamic calendar and outcome',
            pathname: '/academic-calendars',
          },
        ],
      },
      { id: 10, title: 'NIRF', pathname: '/NIRF' },
      { id: 11, title: 'NAAC', pathname: '/NAAC' },
      { id: 12, title: 'Feedback', pathname: '/feedback', inactive: false },
      { id: 13, title: 'Contact us', pathname: '/contactus' },
    ],
    [activeSubNavItem]
  )

  useEffect(() => {
    const handleScroll = () => {
      if (!headerRef.current) return
      const headerTop =
        headerRef.current.offsetTop + headerRef.current.offsetHeight
      const scrollTop = window.scrollY
      setIsFixed(scrollTop > headerTop)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    setActiveNavItem(null)
    setActiveSubNav(null)
  }, [location.pathname])

  // Helper functions
  const handleExpand = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : null)
  }

  const handleDrawerToggle = () => {
    setMobileOpen((prev) => !prev)
  }

  const handleDialogOpen = () => {
    setDialogOpen(true)
  }

  const handleDialogClose = () => {
    setDialogOpen(false)
  }

  // const navigateToSubRoute = (data, deptTitle, subItemTitle) => {
  //   const deptPath = deptTitle.toLowerCase().replace(/\s+/g, '-')
  //   const subItemPath = subItemTitle.title.toLowerCase().replace(/\s+/g, '-')
  //   const selectedData = { College: data, Department: subItemTitle }
  //   navigate(`/department/${deptPath}/${subItemPath}`, {
  //     state: { College_details: selectedData },
  //   })
  //   handleDialogClose()
  // }

  const isMenuItemActive = (item) => {
    // const DepTitle = item.subnav.map((items) => items.title)
    if (item.title === 'Courses') {
      return (
        location.pathname.startsWith('/courses/') &&
        location.pathname.split('/').length > 2
      )
    }

    if (item.subnav_flag) {
      return item.subnav.some(
        (subItem) => location.pathname === subItem.pathname
      )
    }

    return location.pathname === item.pathname
  }

  const handleNavigation = (item, subItem = null) => {
    if (subItem && subItem.title === 'Courses') {
      handleDialogOpen()
      return
    }

    if (subItem) {
      if (!subItem.pathname) {
        console.error('Navigation error: subItem has no pathname', subItem)
        return
      }
      const selected_data = {
        College: item || {},
        CourseOffered: subItem,
      }
      navigate(subItem.pathname, {
        state: { College_details: selected_data },
      })
      setActiveSubNav(subItem.title === activeSubNav ? null : subItem.title)
      return
    }

    if (!item) {
      console.error('Navigation error: item is null')
      return
    }

    if (item.subnav_flag) {
      setActiveNavItem(item.title === activeNavItem ? null : item.title)
    } else if (item.pathname) {
      navigate(item.pathname)
    } else {
      console.error('Navigation error: item has no pathname', item)
    }
  }

  // Component rendering functions
  const renderSubMenu = (subnav, depth = 0) => {
    if (!subnav || !Array.isArray(subnav)) return null

    return (
      <Box
        sx={{
          position: 'absolute',
          top: '100%',
          left: depth > 0 ? '100%' : 0,
          backgroundColor: 'white',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          borderRadius: 1,
          zIndex: 1000,
          padding: '8px 0',
          minWidth: '200px',
        }}
        onMouseEnter={() => setActiveSubNav(subnav.id)}
        onMouseLeave={() => setActiveSubNav(null)}
      >
        {subnav.map(
          (subItem, i) =>
            subItem && (
              <Box key={subItem.title || i} sx={{ position: 'relative' }}>
                <MenuItem
                  onClick={() => handleNavigation(null, subItem)}
                  disabled={subItem.disabled === true}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    '&:hover': {
                      backgroundColor: '#f2f2f2',
                      cursor: 'pointer',
                      zoom: 1.05,
                    },
                  }}
                >
                  {subItem.title || subItem.page_name || 'Untitled'}
                  {subItem.subnav_flag && (
                    <KeyboardArrowDownIcon
                      fontSize="medium"
                      style={{
                        transform:
                          activeSubNav === subItem.title
                            ? 'rotate(180deg)'
                            : 'rotate(0deg)',
                        transition: 'transform 0.2s ease-in-out',
                      }}
                    />
                  )}
                </MenuItem>
                {subItem.subnav &&
                  activeSubNav === subItem.title &&
                  renderNestedMenu(subItem.subnav, depth + 1)}
              </Box>
            )
        )}
      </Box>
    )
  }

  const renderNestedMenu = (subnav, depth = 0) => {
    return (
      <Box
        sx={{
          width: '100%',
          backgroundColor: 'white',
          boxShadow: '0 6px 12px rgba(0, 0, 0, 0.15)',
          borderRadius: 2,
          zIndex: 1000,
          padding: '12px 0',
          minWidth: '220px',
          maxWidth: '100%',
          maxHeight: '400px',
          overflowY: 'auto',
        }}
      >
        {Object.keys(subnav).map((courseType) => (
          <Accordion key={courseType} defaultExpanded={depth === 0}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`${courseType}-content`}
              id={`${courseType}-header`}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: '600',
                  color: 'Black',
                  fontSize: '1rem',
                }}
              >
                {courseType}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {Object.values(subnav[courseType]).map((subItem, i) => (
                <Box key={subItem.pathname} sx={{ position: 'relative' }}>
                  <MenuItem
                    onClick={() => handleNavigation(null, subItem)}
                    disabled={subItem.disabled}
                    sx={{
                      display: 'flex',
                      padding: '8px 16px',
                      fontSize: 'small',
                      '&:hover': {
                        backgroundColor: '#f2f2f2',
                        cursor: 'pointer',
                        zoom: 1.1,
                      },
                      '&.Mui-disabled': {
                        backgroundColor: '#f5f5f5',
                        color: '#b0b0b0',
                      },
                    }}
                  >
                    {subItem.title}
                    <KeyboardArrowRightIcon fontSize="small" />
                  </MenuItem>
                  {subItem.subnav &&
                    renderNestedMenu(subItem.subnav, depth + 1)}
                </Box>
              ))}
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    )
  }

  const renderNavItems = () => {
    return navItems.map((item) => (
      <Box
        key={item?.id || Math.random().toString(36).substring(2, 9)}
        sx={{ position: 'relative' }}
        onMouseEnter={() => {
          if (item?.subnav_flag) {
            setActiveNavItem(item.id)
          } else {
            setActiveNavItem(null)
            setActiveSubNav(null)
          }
        }}
        onMouseLeave={() => {
          setActiveNavItem(null)
          setActiveSubNav(null)
        }}
      >
        <Button
          onClick={() => handleNavigation(item, item.subnav)}
          sx={{
            color: 'white',
            textTransform: 'none',
            padding: '10px',
            borderBottom: isMenuItemActive(item)
              ? '4px solid #F15215'
              : '4px solid transparent',
            fontSize: 'small',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          {item?.title || 'Untitled'}
          {item?.subnav_flag && (
            <KeyboardArrowDownIcon
              sx={{
                marginLeft: 1,
                transform:
                  activeNavItem === item.id ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s ease-in-out',
              }}
            />
          )}
        </Button>
        {item?.subnav_flag &&
          activeNavItem === item.id &&
          renderSubMenu(item.subnav)}
      </Box>
    ))
  }

  const renderMobileMenu = () => {
    const renderNestedItems = (data, level = 0, parentItem = null) => {
      if (Array.isArray(data)) {
        return data.map((subItem, subIndex) => (
          <Box
            key={subIndex}
            sx={{
              pl: level * 2,
              borderLeft: level > 0 ? '1px dashed #e0e0e0' : 'none',
              ml: level > 0 ? 2 : 0,
            }}
          >
            {subItem.subnav_flag ? (
              <Accordion
                disableGutters
                elevation={0}
                sx={{
                  backgroundColor: 'transparent',
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: '#F15215' }} />}
                  sx={{
                    minHeight: '48px !important',
                    px: 1,
                    '& .MuiAccordionSummary-content': {
                      my: 0.5,
                    },
                  }}
                >
                  <Typography
                    sx={{
                      color: 'text.primary',
                      fontWeight: level === 0 ? 'bold' : 'normal',
                    }}
                  >
                    {subItem.title}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  {renderNestedItems(subItem.subnav, level + 1, subItem)}
                </AccordionDetails>
              </Accordion>
            ) : (
              <ListItemButton
                onClick={(e) => {
                  e.preventDefault() // Prevent default navigation
                  if (subItem.title === 'Courses') {
                    handleDialogOpen()
                  } else {
                    const selected_data = {
                      College: parentItem,
                      CourseOffered: subItem,
                    }
                    navigate(subItem.pathname, {
                      state: { College_details: selected_data },
                    })
                    setMobileOpen(false)
                  }
                }}
                sx={{
                  px: 1,
                  borderRadius: 1,
                  background:
                    location.pathname === subItem.pathname
                      ? 'rgba(241, 82, 21, 0.1)'
                      : 'transparent',
                  color:
                    location.pathname === subItem.pathname
                      ? '#F15215'
                      : 'text.primary',
                  '&:hover': {
                    backgroundColor: 'rgba(241, 82, 21, 0.05)',
                  },
                }}
              >
                <ListItemText
                  primary={subItem.title}
                  primaryTypographyProps={{
                    fontWeight:
                      location.pathname === subItem.pathname
                        ? 'bold'
                        : 'normal',
                  }}
                />
              </ListItemButton>
            )}
          </Box>
        ))
      } else if (typeof data === 'object' && !Array.isArray(data)) {
        return Object.entries(data).map(([key, value], i) => (
          <Accordion
            key={i}
            disableGutters
            elevation={0}
            sx={{
              backgroundColor: 'transparent',
              '&:before': { display: 'none' },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: '#F15215' }} />}
              sx={{
                minHeight: '48px !important',
                px: 1,
                '& .MuiAccordionSummary-content': {
                  my: 0.5,
                },
              }}
            >
              <Typography sx={{ color: 'text.primary' }}>{key}</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              {value.map((course, j) => {
                const courseId = course.title.toLowerCase().replace(/\s+/g, '-')
                const courseType = key.toLowerCase()

                return (
                  <ListItemButton
                    key={j}
                    onClick={(e) => {
                      e.preventDefault()
                      const selected_data = {
                        College: {
                          title: 'Courses',
                          pathname: '/academics/courses',
                        },
                        CourseOffered: {
                          ...course,
                          pathname: `/courseoffering/${courseType}/${courseId}`,
                        },
                      }
                      navigate(`/courseoffering/${courseType}/${courseId}`, {
                        state: { College_details: selected_data },
                      })
                      setMobileOpen(false)
                    }}
                    sx={{
                      pl: 4,
                      borderRadius: 1,
                      background:
                        location.pathname ===
                        `/courseoffering/${courseType}/${courseId}`
                          ? 'rgba(241, 82, 21, 0.1)'
                          : 'transparent',
                      color:
                        location.pathname ===
                        `/courseoffering/${courseType}/${courseId}`
                          ? '#F15215'
                          : 'text.primary',
                      '&:hover': {
                        backgroundColor: 'rgba(241, 82, 21, 0.05)',
                      },
                    }}
                  >
                    <ListItemText
                      primary={course.title}
                      primaryTypographyProps={{
                        fontWeight:
                          location.pathname ===
                          `/courseoffering/${courseType}/${courseId}`
                            ? 'bold'
                            : 'normal',
                      }}
                    />
                  </ListItemButton>
                )
              })}
            </AccordionDetails>
          </Accordion>
        ))
      }
      return null
    }

    return (
      <List sx={{ py: 0 }}>
        {navItems.map((item, index) => {
          const isActive = location.pathname === item.pathname
          const isExpanded = expanded === item.title

          return (
            <Box key={index} sx={{ mb: 0.5 }}>
              {item.title === 'Courses' ? (
                <ListItemButton
                  onClick={(e) => {
                    e.preventDefault() // Prevent default navigation
                    handleDialogOpen() // Open the department dialog
                  }}
                  sx={{
                    borderRadius: 1,
                    background: isActive
                      ? 'rgba(241, 82, 21, 0.1)'
                      : 'transparent',
                    color: isActive ? '#F15215' : 'text.primary',
                    '&:hover': {
                      backgroundColor: 'rgba(241, 82, 21, 0.05)',
                    },
                  }}
                >
                  <ListItemText
                    primary={item.title}
                    primaryTypographyProps={{
                      fontWeight: isActive ? 'bold' : 'bold',
                    }}
                  />
                </ListItemButton>
              ) : item.subnav_flag ? (
                <Accordion
                  disableGutters
                  elevation={0}
                  expanded={isExpanded}
                  onChange={handleExpand(item.title)}
                  sx={{
                    backgroundColor: 'transparent',
                    '&:before': { display: 'none' },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: '#F15215' }} />}
                    sx={{
                      minHeight: '48px !important',
                      px: 1,
                      borderRadius: 1,
                      background: isActive
                        ? 'rgba(241, 82, 21, 0.1)'
                        : 'transparent',
                      '& .MuiAccordionSummary-content': {
                        my: 0.5,
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        color: isActive ? '#F15215' : 'text.primary',
                        fontWeight: 'bold',
                      }}
                    >
                      {item.title}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0 }}>
                    {renderNestedItems(item.subnav, 1, item)}
                  </AccordionDetails>
                </Accordion>
              ) : (
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={(e) => {
                      e.preventDefault()
                      navigate(item.pathname)
                      setMobileOpen(false)
                    }}
                    sx={{
                      borderRadius: 1,
                      background: isActive
                        ? 'rgba(241, 82, 21, 0.1)'
                        : 'transparent',
                      color: isActive ? '#F15215' : 'text.primary',
                      '&:hover': {
                        backgroundColor: 'rgba(241, 82, 21, 0.05)',
                      },
                    }}
                  >
                    <ListItemText
                      primary={item.title}
                      primaryTypographyProps={{
                        fontWeight: isActive ? 'bold' : 'bold',
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              )}
            </Box>
          )
        })}
      </List>
    )
  }

  return (
    <Box>
      {/* Top Bar with Phone Numbers */}
      <AppBar
        sx={{
          backgroundColor: '#F15215',
          position: 'relative',
          height: isMobile ? '48px' : '38px',
          width: '100%',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: { xs: '6px 10px', sm: '6px 20px' },
            marginX: { xs: '0.5rem', sm: '2rem', md: '1rem' },
            width: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: { xs: '4px', sm: '8px' },
              justifyContent: { xs: 'center', sm: 'flex-start' },
              textAlign: 'center',
            }}
          >
            <PhoneIcon sx={{ fontSize: { xs: 16, sm: 20, md: 22 } }} />
            <Typography
              variant="body2"
              sx={{
                fontSize: { xs: '10px', sm: '12px', md: '14px' },
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: { xs: 'center', sm: 'flex-start' },
                gap: '5px',
                lineHeight: '1.2',
                '& a': {
                  whiteSpace: 'nowrap',
                  color: 'white',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                },
              }}
            >
              <a href="tel:040-27670675">040 – 2767 06 75</a>/
              <a href="tel:+919100221282">+91 9100221282</a>/
              <a href="tel:+918331041175">+91 8331041175</a>
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: { xs: '100%', sm: 'auto' },
              maxWidth: { xs: '120px', sm: '200px' },
              height: 'auto',
              order: { xs: 1, sm: 2 },
              mb: { xs: 1, sm: 0 },
            }}
          >
            <img
              src={Dost_code}
              alt="CET Code"
              style={{
                width: '100%',
                height: 'auto',
                maxHeight: '36px',
                objectFit: 'contain',
              }}
            />
          </Box>
        </Box>
      </AppBar>
      {/* Logo Bar */}
      <AppBar
        sx={{
          backgroundColor: 'white',
          position: 'relative',
          height: '100px',
          maxWidth: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            paddingX: { xs: '16px', sm: '40px' },
            height: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                height: '100%',
                maxWidth: '200px',
              }}
            >
              <img
                src={FounderImage}
                alt="Founder"
                style={{
                  height: '100%',
                  width: 'auto',
                  objectFit: 'contain',
                }}
              />
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                height: '100%',
                marginLeft: '16px',
              }}
            >
              <img
                src={logo}
                alt="College Logo"
                style={{
                  height: '80%',
                  maxHeight: '100px',
                  width: '100%',
                  objectFit: 'contain',
                }}
              />
            </Box>
          </Box>

          {!isSmallScreen && !isTablet && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <img
                src={CollegeCertifications}
                alt="College Certifications"
                style={{
                  height: '80%',
                  maxHeight: '100px',
                  objectFit: 'contain',
                }}
              />
            </Box>
          )}
        </Box>
      </AppBar>
      {/* Navigation Bar */}
      <Box
        className="third-app-bar"
        ref={headerRef}
        sx={{
          display: 'flex',
          justifyContent:
            isMobile || isTablet
              ? // || isLargeScreen
                'end'
              : 'space-between',
          backgroundColor: '#2391d0',
          position: isFixed ? 'fixed' : 'relative',
          top: isFixed ? 0 : 'unset',
          left: 0,
          right: 0,
          zIndex: 1100,
          padding: { xs: '0 8px', sm: '0 16px', md: '0 24px' },
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Box display="flex" justifyContent="end" sx={{ width: '100%' }}>
          {isMobile || isTablet ? (
            // || isLargeScreen
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                padding: '8px 0',
              }}
            >
              <IconButton
                aria-label="open main menu"
                onClick={handleDrawerToggle}
                sx={{
                  color: 'white',
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                  padding: '8px',
                  borderRadius: '4px',
                }}
              >
                <MenuIcon fontSize="large" />
              </IconButton>
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                margin: '0 auto',
              }}
            >
              {renderNavItems()}
            </Box>
          )}
        </Box>

        {/* Mobile Drawer */}
        <Drawer
          variant="temporary"
          anchor="right"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
            BackdropProps: {
              sx: {
                backgroundColor: 'rgba(0,0,0,0.5)',
                backdropFilter: 'blur(4px)',
              },
            },
          }}
          sx={{
            '& .MuiDrawer-paper': {
              width: { xs: '85vw', sm: '400px', md: '450px' },
              boxSizing: 'border-box',
              px: 2,
              py: 1,
              backgroundColor: '#f8f8f8',
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 1,
              borderBottom: '1px solid #e0e0e0',
              pb: 1,
            }}
          >
            <IconButton
              onClick={handleDrawerToggle}
              sx={{
                color: '#F15215',
                '&:hover': { backgroundColor: 'rgba(241, 82, 21, 0.1)' },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          {renderMobileMenu()}
        </Drawer>
      </Box>
      {/* Department Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => {
          handleDialogClose()
          setMobileOpen(false)
        }}
        maxWidth="md"
        fullWidth
        BackdropProps={{
          style: {
            backgroundColor: 'rgba(0,0,0,0.7)',
          },
        }}
        PaperProps={{
          sx: {
            width: isMobile ? '95%' : '100%',
            margin: 'auto',
            borderRadius: isMobile ? 2 : 4,
            background: 'linear-gradient(to bottom, #f9fbfd 0%, #ffffff 100%)',
            boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
            overflow: 'hidden',
            border: '1px solid rgba(0,0,0,0.08)',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {/* Sticky Header with Close Button */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 2,
            py: 1,
            background: 'rgba(232, 232, 232, 0.25)',
          }}
        >
          <Typography
            variant="h5"
            sx={{
              textDecoration: 'none',
            }}
            fontWeight={600}
          >
            Academic Courses
          </Typography>
          <IconButton onClick={handleDialogClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Tabs
          value={deptTab}
          onChange={(e, val) => setDeptTab(val)}
          variant="fullWidth"
          textColor="primary"
          indicatorColor="primary"
          sx={{
            position: 'sticky',
            zIndex: 1,
            backgroundColor: '#fff',
            boxShadow: 'inset 0 -1px 0 rgba(0,0,0,0.1)',
            '& .MuiTabs-indicator': {
              height: 2,
              backgroundColor: ' #F15215',
              borderRadius: '2px 2px 0 0',
            },
            '& .MuiTab-root': {
              fontWeight: 600,
              fontSize: '0.95rem',
              textTransform: 'none',
              minHeight: 52,
              color: '#444',
              '&.Mui-selected': {
                color: '  #F15215 ',
              },
            },
          }}
        >
          <Tab
            icon={<SchoolIcon />}
            iconPosition="start"
            value="UG"
            label={
              <Box>
                Undergraduate
                <Typography variant="caption" display="block">
                  (UG Courses)
                </Typography>
              </Box>
            }
          />
          <Tab
            icon={<WorkspacePremiumIcon />}
            iconPosition="start"
            value="PG"
            label={
              <Box>
                Postgraduate
                <Typography variant="caption" display="block">
                  (PG Courses)
                </Typography>
              </Box>
            }
          />
        </Tabs>
        {/* Content */}
        <Box sx={{ maxHeight: '70vh', overflowY: 'auto', px: 2, pb: 3, pt: 2 }}>
          <Grid container spacing={2}>
            {departmentData[deptTab].map((course) => (
              <Grid item xs={12} key={course.title}>
                <Box
                  sx={{
                    border: '1px solid rgba(224, 224, 224, 0.71)',
                    borderRadius: 2,
                    overflow: 'hidden',
                    // boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
                    transition: 'box-shadow 0.3s',
                    positon: 'relative',
                    '&:hover': {
                      boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      background: 'rgba(232, 232, 232, 0.25)',
                      p: 2,
                      display: 'flex',
                      alignItems: 'center',
                      borderBottom: '1px solid #e0e0e0',
                      '&:hover': {
                        color: 'white',
                      },
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      sx={{
                        flexGrow: 1,
                      }}
                    >
                      {course.title}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#555',
                        bgcolor: 'rgba(35,145,208,0.1)',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 10,
                        fontWeight: 500,
                      }}
                    >
                      {course.subdepartment.length} Courses
                    </Typography>
                  </Box>

                  <Box sx={{ p: 2, backgroundColor: '#fff' }}>
                    <Grid container spacing={1}>
                      {course.subdepartment.map((sub) => (
                        <Grid item xs={12} sm={6} key={sub.title}>
                          <Box
                            sx={{
                              p: 1.5,
                              borderRadius: 1,
                              backgroundColor: '#f9f9f9',
                              display: 'flex',
                              alignItems: 'flex-start',
                              transition: 'all 0.2s',
                              '&:hover': {
                                backgroundColor: '#e3f2fd',
                                transform: 'translateX(4px)',
                              },
                            }}
                          >
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                bgcolor: 'rgba(241, 83, 21, 0.61) ',
                                borderRadius: '50%',
                                mt: 0.75,
                                mr: 1.5,
                                flexShrink: 0,
                              }}
                            />
                            <Box>
                              <Typography
                                variant="body2"
                                sx={{ lineHeight: 1.5 }}
                              >
                                {sub.title}
                              </Typography>
                              {sub.subtitle && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ display: 'block', mt: 0.5 }}
                                >
                                  {sub.subtitle}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Dialog>
    </Box>
  )
}

export default Header
